import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { BulkMessagesQueries } from '@frontend/api-messaging';
import { MessagingEtlAmpQueries } from '@frontend/api-messaging-etl-amp';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Accordion, SpinningLoader, Text, Tray } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { FilterAccordion } from './accordion';
import { AgeFilter } from './age-filter';
import { AppointmentDateFilter } from './appointment-date-filter';
import { DateFilter } from './date-filter';
import { DropdownFilter } from './dropdown-filter';
import { FiltersViewFooter } from './filters-view-footer';
import { FiltersViewHeader } from './filters-view-header';
import { MultiselectFilter } from './multiselect-filter';
import { useFiltersView } from './use-filters-view';
import { formatRange } from './utils/format-range';

export const FiltersView = () => {
  const { t } = useTranslation('bulk-messaging');
  const { campaignType, filters, locationIds, setFilters } = useAudienceTray();
  const { applyFilters, cancelFilters, resetFilters, resetKey } = useFiltersView();
  const { data: filterOptions = [], isLoading: isFiltersLoading } = BulkMessagesQueries.useRecipientFilterOptions();
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { data: organizationsData = [] } = MessagingEtlAmpQueries.useGetLocation({ orgId });
  const locations = locationIds.map((locationId) =>
    organizationsData.find((location) => location.locationId === locationId)
  );

  const data = useMemo(() => {
    const options = {
      appointmentPractitioner: filterOptions.find((option) => option.filterType === 'APPOINTMENT_PRACTITIONER'),
      appointmentStatus: filterOptions.find((option) => option.filterType === 'APPOINTMENT_STATUS'),
      appointmentType: filterOptions.find((option) => option.filterType === 'APPOINTMENT_TYPE'),
      lastReceivedBulkMessageOptions: ['30+ days', '60+ days', '90+ days', '120+ days', '180+ days'],
      locations,
      primaryInsurance: filterOptions.find((option) => option.filterType === 'PRIMARY_INSURANCE'),
      recallType: filterOptions.find((option) => option.filterType === 'RECALL_TYPE'),
      recipientStatus: filterOptions.find((option) => option.filterType === 'RECIPIENT_STATUS'),
      // sourceTenant: filterOptions.find((option) => option.filterType === 'SOURCE_TENANT'),
    };

    return {
      ...options,
      appointmentPractitionerOptions: Object.values(options.appointmentPractitioner?.filterOptions ?? {}),
      appointmentStatusOptions:
        Object.values(options.appointmentStatus?.filterOptions ?? {}).filter((item) => item) ?? [],
      appointmentTypeOptions: Object.values(options.appointmentType?.filterOptions ?? {}).filter((item) => item) ?? [],
      locationsOptions: locations
        .map((location) => location?.name)
        .filter((location): location is NonNullable<typeof location> => !!location),
      primaryInsuranceOptions:
        Object.values(options.primaryInsurance?.filterOptions ?? {}).filter((item) => item) ?? [],
      recallTypeOptions: Object.values(options.recallType?.filterOptions ?? {}).filter((item) => item) ?? [],
      recipientStatusOptions: Object.values(options.recipientStatus?.filterOptions ?? {}).filter((item) => item) ?? [],

      numberOfAppointmentStatuses: Object.keys(options.appointmentStatus?.filterOptions ?? {}).length,
      numberOfAppointmentTypes: Object.keys(options.appointmentType?.filterOptions ?? {}).length,
      numberOfLocations: locations.length,
      numberOfPrimaryInsurances: Object.keys(options.primaryInsurance?.filterOptions ?? {}).length,
      numberOfRecallTypes: Object.keys(options.recallType?.filterOptions ?? {}).length,
      numberOfRecipientStatuses: Object.keys(options.recipientStatus?.filterOptions ?? {}).length,
      numberOfAppointmentPractitioners: Object.keys(options.appointmentPractitioner?.filterOptions ?? {}).length,
      // numberOfIntegrations: Object.keys(sourceTenant?.filterOptions ?? {}).length,
    };
  }, [filterOptions, locations]);

  return (
    <>
      <FiltersViewHeader cancelFilters={cancelFilters} />
      <Tray.Body
        css={{
          padding: theme.spacing(0, 4, 2, 4),
        }}
      >
        {isFiltersLoading ? (
          <div css={{ textAlign: 'center', padding: theme.spacing(4, 4, 0) }}>
            <SpinningLoader />
          </div>
        ) : (
          <div css={{ paddingTop: theme.spacing(4) }}>
            <Accordion
              variant='hybrid'
              color={theme.colors.neutral10}
              //TODO: Fix the accordion component in the design system so styles can be accessed more easily. Maybe some of this visibility stuff could be used in the base component?
              css={{
                '> div[data-active="true"]': {
                  overflow: 'visible',
                  'section[role="region"]': {
                    overflow: 'visible',
                  },
                },
                '> div > h3 > button': {
                  border: `1px solid ${theme.colors.neutral10}`,
                  backgroundColor: theme.colors.white,
                  borderRadius: theme.borderRadius.medium,
                },
                '> div[data-active="true"] > h3 > button': {
                  backgroundColor: theme.colors.neutral10,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  border: `1px solid ${theme.colors.neutral20}`,
                },
              }}
            >
              {data.numberOfAppointmentStatuses || data.numberOfAppointmentTypes ? (
                <FilterAccordion.Item value='appointment-accordion'>
                  <FilterAccordion.Header
                    hasFilters={
                      !!filters.appointmentQualifier ||
                      !!filters.appointmentTypes ||
                      !!filters.appointmentStatuses ||
                      !!filters.appointmentBeginDate ||
                      !!filters.appointmentEndDate
                    }
                    label={'Appointment Details'}
                  />
                  <FilterAccordion.Body>
                    <AppointmentDateFilter
                      appointmentDateRange={[filters.appointmentBeginDate ?? '', filters.appointmentEndDate ?? '']}
                      appointmentQualifier={filters.appointmentQualifier}
                      onChangeAppointmentDateRange={(range) => {
                        const formattedRange = formatRange(range);
                        setFilters((prev) => ({
                          ...prev,
                          appointmentBeginDate: formattedRange?.[0],
                          appointmentEndDate: formattedRange?.[1],
                        }));
                      }}
                      onChangeAppointmentQualifier={(appointmentQualifier) =>
                        setFilters((prev) => ({ ...prev, appointmentQualifier }))
                      }
                      resetKey={resetKey}
                    />
                    {data.numberOfAppointmentTypes ? (
                      <>
                        <MultiselectFilter
                          label={t('Appointment Type')}
                          name='appointment-type'
                          onChange={(appointmentTypes) => setFilters((prev) => ({ ...prev, appointmentTypes }))}
                          options={data.appointmentTypeOptions}
                          resetKey={resetKey}
                          value={filters.appointmentTypes}
                        />
                        <Divider margin={theme.spacing(1)} />
                      </>
                    ) : null}

                    {data.numberOfAppointmentStatuses ? (
                      <MultiselectFilter
                        label={t('Appointment Status')}
                        name='appointment-status'
                        onChange={(appointmentStatuses) => setFilters((prev) => ({ ...prev, appointmentStatuses }))}
                        options={data.appointmentStatusOptions}
                        resetKey={resetKey}
                        value={filters.appointmentStatuses}
                      />
                    ) : null}
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}
              {data.numberOfRecallTypes ? (
                <FilterAccordion.Item value='recall-accordion'>
                  <FilterAccordion.Header
                    hasFilters={!!filters.recallTypes || !!filters.recallBeginDate || !!filters.recallEndDate}
                    label={t('Recall Details')}
                  />
                  <FilterAccordion.Body>
                    <MultiselectFilter
                      label={t('Recall Type')}
                      name='recall-type'
                      onChange={(recallTypes) => setFilters((prev) => ({ ...prev, recallTypes }))}
                      options={data.recallTypeOptions}
                      resetKey={resetKey}
                      value={filters.recallTypes}
                    />
                    <Text size='small'>{t('Recall Date')}</Text>
                    <DateFilter
                      name='recall-date'
                      onChange={(range) => {
                        const formattedRange = formatRange(range);
                        return setFilters((prev) => ({
                          ...prev,
                          recallBeginDate: formattedRange?.[0],
                          recallEndDate: formattedRange?.[1],
                        }));
                      }}
                      resetKey={resetKey}
                      value={[filters.recallBeginDate ?? '', filters.recallEndDate ?? '']}
                    />
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}
              {data.numberOfLocations > 1 ? (
                <FilterAccordion.Item value='location-accordion'>
                  <FilterAccordion.Header hasFilters={!!filters.locationIds} label={t('Locations')} />
                  <FilterAccordion.Body>
                    <MultiselectFilter
                      label={t('Locations')}
                      name='locations'
                      onChange={(locationNames) =>
                        setFilters((prev) => ({
                          ...prev,
                          locationIds:
                            locationNames
                              ?.map((name) => data.locations.find((location) => location?.name === name)?.locationId)
                              .filter((locationId): locationId is NonNullable<typeof locationId> => !!locationId) ?? [],
                        }))
                      }
                      options={data.locationsOptions}
                      resetKey={resetKey}
                      value={
                        filters.locationIds
                          ?.map(
                            (locationId) => data.locations.find((location) => location?.locationId === locationId)?.name
                          )
                          .filter((locationName): locationName is NonNullable<typeof locationName> => !!locationName) ??
                        []
                      }
                    />
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}
              {data.numberOfPrimaryInsurances ? (
                <FilterAccordion.Item value='insurance-accordion'>
                  <FilterAccordion.Header hasFilters={!!filters.primaryInsurances} label={t('Insurance Details')} />
                  <FilterAccordion.Body>
                    <MultiselectFilter
                      label={t('Insurance Type')}
                      name='insurance-type'
                      onChange={(primaryInsurances) => setFilters((prev) => ({ ...prev, primaryInsurances }))}
                      options={data.primaryInsuranceOptions}
                      resetKey={resetKey}
                      value={filters.primaryInsurances}
                    />
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}
              {data.numberOfRecipientStatuses ? (
                <FilterAccordion.Item value='recipient-accordion'>
                  <FilterAccordion.Header
                    hasFilters={!!filters.recipientStatuses || !!filters.minAge || !!filters.maxAge}
                    label={t('Recipient Details')}
                  />
                  <FilterAccordion.Body>
                    <MultiselectFilter
                      label={t('Recipient Status')}
                      name='recipient-status'
                      onChange={(recipientStatuses) => setFilters((prev) => ({ ...prev, recipientStatuses }))}
                      options={data.recipientStatusOptions}
                      resetKey={resetKey}
                      value={filters.recipientStatuses}
                    />
                    <Divider margin={theme.spacing(1)} />
                    <Text size='small'>{t('Recipient Age')}</Text>
                    <AgeFilter
                      maxAge={filters.maxAge}
                      maxAgeLabel={t('Maximum Age')}
                      minAge={filters.minAge}
                      minAgeLabel={t('Minimum Age')}
                      onChangeMaxAge={(maxAge) => setFilters((prev) => ({ ...prev, maxAge }))}
                      onChangeMinAge={(minAge) => setFilters((prev) => ({ ...prev, minAge }))}
                      resetKey={resetKey}
                    />
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}
              {data.numberOfAppointmentPractitioners ? (
                <FilterAccordion.Item value='appointment-practitioners-accordian'>
                  <FilterAccordion.Header
                    hasFilters={!!filters.appointmentPractitioners}
                    label={t('Provider Details')}
                  />
                  <FilterAccordion.Body>
                    <MultiselectFilter
                      label={t('Provider Name')}
                      name='provider-details'
                      onChange={(appointmentPractitioners) =>
                        setFilters((prev) => ({ ...prev, appointmentPractitioners }))
                      }
                      options={data.appointmentPractitionerOptions}
                      resetKey={resetKey}
                      value={filters.appointmentPractitioners}
                    />
                  </FilterAccordion.Body>
                </FilterAccordion.Item>
              ) : null}

              {/* TODO: Confirm with backend it is safe to use minDaysSinceLastMarketingMessage and this is the correct way to do it.
              For more details, see: https://getweave.slack.com/archives/C06M3770CEM/p1718304588020749?thread_ts=1718301498.597029&cid=C06M3770CEM */}
              <FilterAccordion.Item value='by-last-received-bulk-message'>
                <FilterAccordion.Header
                  hasFilters={!!filters.minDaysSinceLastMarketingMessage}
                  label={
                    campaignType === CampaignType.EMAIL
                      ? t('Last Received Email Campaign')
                      : t('Last Received Text Campaign')
                  }
                />
                <FilterAccordion.Body>
                  <DropdownFilter
                    label={
                      campaignType === CampaignType.EMAIL
                        ? t('Last Received Email Campaign')
                        : t('Last Received Text Campaign')
                    }
                    name='last-received-bulk-message'
                    onChange={(selection) =>
                      setFilters((prev) => ({
                        ...prev,
                        minDaysSinceLastMarketingMessage: selection ? +selection.replace(/\D/g, '') : undefined,
                      }))
                    }
                    options={data.lastReceivedBulkMessageOptions}
                    value={filters.minDaysSinceLastMarketingMessage?.toString()} // TODO: Passed selected values do not show the correct label
                    resetKey={resetKey}
                  />
                </FilterAccordion.Body>
              </FilterAccordion.Item>
              {/* TODO: Should the "Provider Location" filter be added? Is it missing? */}
            </Accordion>
          </div>
        )}
      </Tray.Body>
      <FiltersViewFooter applyFilters={applyFilters} cancelFilters={cancelFilters} resetFilters={resetFilters} />
    </>
  );
};
